import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { MessageService } from '../message.service';
import { IRoughcheck, IAssessment } from '../roughcheck';
import { RoughcheckService } from '../roughcheck.service';
import { LoggerService } from '../logger.service';

/**
 * AssessmentComponent to capture user information for an assessment
 */
@Component({
  selector: 'app-assessment',
  templateUrl: './assessment.component.html',
})
export class AssessmentComponent implements OnInit {

  roughcheck: IRoughcheck = {
    rref: '',
    title: '',
    image: '',
    image_alt: '',
    category: '',
    riskprofile: '',
    rewardprofile: '',
    show_risk_reward: 1,
    description: '',
    slug: '',
    status: 0,
    status_email: 0,
    feedback: 0,
    sponsor: '',
    website: ''
  };
  assessment: IAssessment = {aref: ''};

  profileForm = this.fb.group({
    title: [''],
    uref: [''],
    description: [''],
    email: [''],
  }, {updateOn: 'blur'});

  constructor(
    private fb: FormBuilder,
    private service: RoughcheckService,
    private logger: LoggerService,
    private message: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title
  ) { }

  /**
   * If existing, fill in from server
   * and allow user to view sections, report and actions
   * else this is the assessment start page
   */
  ngOnInit() {
    this.title.setTitle( 'Roughcheck assessment information' );
    this.roughcheck = this.service.getRoughcheck();
    if (! this.roughcheck.rref) {
      this.service.gotoFromUrl('assessment', this.route.snapshot);
      return;
    }

    // If this is an existing assessment, get the assessment
    if (this.service.getARef()) {
      this.assessment = this.service.getAssessment();
    }
    // Patch the form with assessment details
    this.profileForm.patchValue({
      title: this.assessment.title,
      uref: this.assessment.uref,
      description: this.assessment.description,
      email: this.assessment.email,
    });

    if (this.roughcheck.status_email === 2) {
      // * 2 Required
      this.profileForm.controls.email.setValidators([
        Validators.required,
        Validators.email
      ]);
      this.profileForm.controls.email.updateValueAndValidity();
    } else if (this.roughcheck.status_email === 1) {
      // * 1 Optional
      this.profileForm.controls.email.setValidators(Validators.email);
      this.profileForm.controls.email.updateValueAndValidity();
    } else {
      // * 0 Not required
      this.profileForm.controls.email.clearValidators();
      this.profileForm.controls.email.updateValueAndValidity();
    }

    // Save values as they are entered
    this.profileForm.controls.title.valueChanges.subscribe(value => {
      this.assessment.title = value;
      if (this.assessment.aref) {
        this.Save();
      }
    });
    this.profileForm.controls.uref.valueChanges.subscribe(value => {
      this.assessment.uref = value;
      if (this.assessment.aref) {
        this.Save();
      }
    });
    this.profileForm.controls.description.valueChanges.subscribe(value => {
      this.assessment.description = value;
      if (this.assessment.aref) {
        this.Save();
      }
    });
    this.profileForm.controls.email.valueChanges.subscribe(value => {
      this.assessment.email = value;
      if (this.assessment.aref) {
        this.Save();
      }
    });
  }

  /**
   * Create new or update existing assessment
   * then navigate to the sections
   * Nothing is currently required, any defaults will be returned
   */
  Save() {
    if (this.profileForm.valid){
      this.assessment.uref = this.profileForm.get('uref').value;
      this.assessment.title = this.profileForm.get('title').value;
      this.assessment.description = this.profileForm.get('description').value;
      this.assessment.email = this.profileForm.get('email').value;
      this.service.setHttpAssessment(this.assessment)
        .subscribe(
          (resp) => this.handleSuccess(resp),
          (error) => this.service.handleError(error)
        );
    }
  }

  /**
   * Create new or update existing assessment
   * then navigate to the sections
   * Nothing is currently required, any defaults will be returned
   */
  onSubmit() {
    if (this.profileForm.valid){
      this.Save();
      this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'sections']);
    }
  }

  /**
   * Go to the Roughcheck start page
   */
  onGoBack() {
    if (this.service.getARef()) {
      this.router.navigate(['/a', this.service.getRRef(), this.service.getARef()]);
    } else {
      this.router.navigate(['/a', this.service.getRRef()]);
    }
  }


  /**
   * Populate Roughcheck assessment
   */
  private handleSuccess(resp: HttpResponse<any>) {
    this.assessment = {
      aref: resp.body.aref,
      uref: resp.body.uref,
      title: resp.body.title,
      email: resp.body.email,
      description: resp.body.description,
      date_completed: resp.body.date_completed,
      status: resp.body.status
    };
    this.service.setAssessment(this.assessment);
    this.title.setTitle( this.assessment.title );
    if (this.service.getARef()) {
      this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'sections']);
    }

  }

}
